import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EventModel } from '@app/events/shared/event.model';
import { EventAccommodationService } from '@app/events/shared/event-accommodation.service';
import {
  AccommodationDataModel,
  AccommodationRoomModel,
} from '@app/events/shared/event-accommodation.model';
import { EventGuestModel } from '@app/events/shared/event-guest.model';
import { ToastService } from '@app/shared/toast/toast.service';
import { AccommodationModalComponent, AccommodationModalContext } from './accommodation-modal.component';

@Component({
  selector: 'app-event-accommodation',
  standalone: true,
  imports: [CommonModule, AccommodationModalComponent],
  templateUrl: './accommodation.component.html',
})
export class AccommodationComponent implements OnInit, OnChanges {
  @Input({ required: true }) event!: EventModel;
  // Customer portal calls a different API base path. Default false = admin.
  @Input() customerPortal = false;

  protected loading = false;
  protected canManage = false;
  protected data?: AccommodationDataModel;

  protected modalOpen = false;
  protected modalContext?: AccommodationModalContext;

  constructor(
    private accommodationService: EventAccommodationService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['event'] && !changes['event'].firstChange) {
      this.reload();
    }
  }

  protected reload(): void {
    if (!this.event?.id) {
      return;
    }

    this.loading = true;
    this.accommodationService.getAccommodation(this.event.id, this.customerPortal).subscribe({
      next: (response) => {
        this.data = response.data;
        this.canManage = response.meta?.permissions?.can_manage_allocations ?? false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.error('Unable to load accommodation data.');
      },
    });
  }

  protected openAddForRoom(room: AccommodationRoomModel): void {
    if (!this.canManage || !this.data) {
      return;
    }

    this.modalContext = {
      mode: 'add',
      roomNumber: room.number,
      roomLabel: this.formatRoomLabel(room),
      candidates: this.data.unallocated_guests,
      guest: null,
    };
    this.modalOpen = true;
  }

  protected openEdit(room: AccommodationRoomModel, guest: EventGuestModel): void {
    if (!this.canManage) {
      return;
    }

    this.modalContext = {
      mode: 'edit',
      roomNumber: room.number,
      roomLabel: this.formatRoomLabel(room),
      candidates: [],
      guest,
    };
    this.modalOpen = true;
  }

  protected openFromUnallocated(guest: EventGuestModel): void {
    if (!this.canManage) {
      return;
    }

    this.modalContext = {
      mode: 'add',
      roomNumber: null,
      roomLabel: null,
      candidates: this.data?.unallocated_guests ?? [],
      guest,
    };
    this.modalOpen = true;
  }

  protected onModalClose(): void {
    this.modalOpen = false;
    this.modalContext = undefined;
  }

  protected onAllocationSaved(): void {
    this.modalOpen = false;
    this.modalContext = undefined;
    this.reload();
  }

  protected formatRoomLabel(room: AccommodationRoomModel): string {
    const parts = [`Room ${room.number}`];
    if (room.name) {
      parts.push(room.name);
    }
    return parts.join(' — ');
  }

  protected guestContact(guest: EventGuestModel): string {
    return [guest.mobile, guest.email].filter((value) => !!value).join(' · ');
  }

  protected get showEmptyTabState(): boolean {
    return !!this.data && (!this.data.has_accommodation_tab || !this.data.venue_has_rooms);
  }
}
